/**
 * `/powitanie` — the trainer welcome. A wiring declaration.
 *
 * Outside `_shell` on purpose: the welcome is a full-screen stage with no navigation, and nothing of
 * the signed-in app is mounted behind it. It carries the shell's guard itself, so it is trainer-only
 * exactly as every screen under the shell is. Reached from the end of a new trainer's registration
 * (`pages/complete-profile`) and from „Zobacz powitanie" on `/konto` — never by itself, so a trainer
 * who registered before it existed does not meet it unasked.
 *
 * `?tryb=krok` or `?tryb=ciag` sets the „Autoodtwarzanie" switch's starting state for one visit.
 *
 * Both screens that lead here PRELOAD it (`router.preloadRoute` on the registration form,
 * `preload="render"` on the account row), so the route's chunk and the welcome's two sounds are in
 * hand before the trainer arrives: the opening gives its tone only a short grace, and a cold fetch
 * would miss it. A preload is not an entry, so the guard lets it through — it fetches static
 * assets and no data — and the guard still decides every real navigation. The loader starts the
 * decode and returns at once: the stage never waits on its sound.
 */

import { createFileRoute, redirect } from '@tanstack/react-router';

import { sessionRedirectFrom } from '~/entities/session';
import { WelcomeScreen } from '~/pages/welcome';

export const Route = createFileRoute('/powitanie')({
  validateSearch: (search: Record<string, unknown>): { tryb?: 'krok' | 'ciag' } =>
    search.tryb === 'krok' || search.tryb === 'ciag' ? { tryb: search.tryb } : {},
  beforeLoad: ({ preload }) => {
    if (preload) return;
    const destination = sessionRedirectFrom('/');
    if (destination !== null) redirect({ to: destination, replace: true, throw: true });
  },
  loader: () => {
    // Imported, not bundled with the route's declaration: the welcome stays in its own chunk.
    void import('~/pages/welcome').then(({ preloadWelcomeSounds }) => preloadWelcomeSounds()).catch(() => undefined);
  },
  component: WelcomeRoute,
});

function WelcomeRoute() {
  const search = Route.useSearch();
  return <WelcomeScreen tryb={search.tryb} />;
}
